import React from "react"
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom"

import { useAuth } from "hooks/useAuth"
import { privateRoutes, publicRoutes } from "router/routes"
import { isDefined } from "utils/validation"
import { Entity, User } from "models"

export const isAnAuthorizedEntity = (entity: Entity, path: string) => {
	const entityRole = entity?.entity_data?.entity_permission?.role
	if (!isDefined(entityRole)) return false

	const currentPath = Object.values({ ...privateRoutes, ...publicRoutes }).find(
		(access) => access.path === path
	)
	if (!currentPath) return true

	return currentPath.entityRoles.includes(entityRole)
}

export const isAnAuthorizedUser = (user: User, path: string) => {
	const userRole = user?.permission
	if (!isDefined(userRole)) return false

	const currentPath = Object.values({ ...privateRoutes, ...publicRoutes }).find(
		(access) => access.path === path
	)
	if (!currentPath) return true

	return currentPath.userRoles.includes(userRole)
}

// A wrapper for <Route> that redirects to the login
// screen if you're not yet authenticated.
export const PrivateRoute = () => {
	const { isLoggedIn, user, entity, isUserDataCompleted } = useAuth()
	const navigate = useNavigate()
	const { pathname } = useLocation()

	React.useEffect(() => {
		// isLoggedIn is an async state, so we need to wait for it to be set
		// we use isUserDataCompleted to know if the state isLoggedIn is set
		if (isUserDataCompleted && !isLoggedIn) navigate(publicRoutes.login.path)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isUserDataCompleted, isLoggedIn])

	if (!isUserDataCompleted) return null

	if (isLoggedIn) {
		if (isAnAuthorizedEntity(entity, pathname) && isAnAuthorizedUser(user, pathname)) {
			return <Outlet />
		} else {
			return <Navigate to={privateRoutes.notAuthorized.path} />
		}
	}

	return (
		<div className="u-sticky-view">
			<div className="c-main-content flex justify-center items-center">
				<div className="c-spinner"></div>
			</div>
		</div>
	)
}
